<template>
    <div class="menu-list">
        <ul class="menu-full">
            <li class="menu-category"
                v-for="category in renderedCategories"
                :key="category.category_id"
                ref="itemRefs"
                :id="category.id">
                <div class="menu-category-name">{{ category.name }}</div>
                <p v-if="!searching && category.description" class="menu-category-description">
                    {{ category.description }}
                </p>
                <ul>
                    <MenuItem v-for="item in mainItems(category)" :item="item" :key="item.id" />
                </ul>
                <div v-for="subcategory in visibleSubcategories(category)" class="menu-subcategory" :key="subcategory.category_id">
                    <p class="menu-subcategory-name">{{ subcategory.name }}</p>
                    <ul>
                        <MenuItem v-for="item in subItems(subcategory)" :item="item" :key="item.id" />
                    </ul>
                </div>
            </li>
        </ul>

        <div v-if="noResults" class="menu-no-results">
            <span class="menu-no-results__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
                    <path d="M20 20L16.5 16.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
            </span>
            <div class="menu-no-results__title">{{ $t('pages.menu.noResults') }}</div>
            <div class="menu-no-results__sub">{{ $t('pages.menu.noResultsSub') }}</div>
        </div>
    </div>
</template>

<script>
import MenuItem from "./MenuItem.vue";

export default {
    name: "MenuInfScroll",
    components: {
        MenuItem,
    },
    props: {
        query: { type: String, default: '' },
    },
    mounted() {
        this.$store.dispatch('menuStore/GET_FULL_MENU')
            .then(() => this.menuLoaded());
    },
    computed: {
        computedMenu() {
            return this.$store.getters['menuStore/getMenu'] || [];
        },
        searching() {
            return this.query.trim().length > 0;
        },
        renderedCategories() {
            if (!this.searching) return this.computedMenu;
            return this.computedMenu.filter(cat =>
                this.mainItems(cat).length > 0 || this.visibleSubcategories(cat).length > 0
            );
        },
        noResults() {
            return this.searching && this.renderedCategories.length === 0;
        }
    },
    watch: {
        // When the search is cleared the full category set returns — re-register
        // the section refs so the parent's scroll-spy observer tracks them again.
        query(val) {
            if (!val.trim()) {
                this.$nextTick(() => this.$emit('loadedItems', this.$refs.itemRefs || []));
            }
        }
    },
    methods: {
        menuLoaded() {
            this.$nextTick(() => {
                const res = this.$refs.itemRefs || [];
                this.$emit("loadedItems", res);
            });
        },
        match(item) {
            if (!this.searching) return true;
            const q = this.query.trim().toLowerCase();
            return (item.name && item.name.toLowerCase().includes(q))
                || (item.description && item.description.toLowerCase().includes(q));
        },
        // De-duplicates: items that belong to a subcategory are not shown in the parent list.
        filteredItems(category) {
            let res = category.items;

            if (category.subcategories.length !== 0) {
                const subItems = category.subcategories.reduce((arr, val) => {
                    return arr.concat(val.items);
                }, []);

                res = category.items.filter(item => {
                    return !subItems.some(subItem => subItem.category_id === item.category_id);
                });
            }

            return res;
        },
        mainItems(category) {
            return this.filteredItems(category).filter(this.match);
        },
        subItems(subcategory) {
            return subcategory.items.filter(this.match);
        },
        visibleSubcategories(category) {
            return (category.subcategories || []).filter(sub => this.subItems(sub).length > 0);
        },
    }
}
</script>

<style lang="scss" scoped>
ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
}

/* The list overflows VContainer (height:100%), which makes the scroll
   container's own padding-bottom ineffective. So the clearance for the floating
   bottom nav must live on the list itself — sized to fully clear the dock
   (~80px footprint) plus breathing room. */
.menu-list {
    padding-bottom: calc(var(--nav-height) + var(--nav-float-gap) * 2 + var(--safe-bottom) + 24px);
}

.menu-category {
    padding-top: 18px;
    /* clears the sticky frosted header so the title isn't hidden under it */
    scroll-margin-top: 112px;
}

.menu-category-name {
    margin: 10px 0;

    font-weight: var(--weight-black);
    font-size: 19px;
    line-height: 1.1;
    letter-spacing: -0.01em;

    color: var(--text-primary);
}

.menu-subcategory-name {
    margin: 22px 0 10px;

    font-weight: var(--weight-bold);
    font-size: 12px;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    color: var(--text-muted);
}

.menu-category-description {
    margin: 12px 0;
    padding: 14px 16px;

    font-weight: var(--weight-regular);
    font-size: 14px;
    line-height: 1.4;
    text-align: center;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-xl);

    color: var(--text-secondary);
    background: var(--surface-card);
}

.menu-no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;

    padding: 56px 24px;
}

.menu-no-results__icon {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 72px;
    height: 72px;

    border-radius: 50%;

    background: var(--color-primary-soft);
    color: var(--color-primary);
}

.menu-no-results__title {
    font-size: 18px;
    font-weight: var(--weight-bold);
    color: var(--text-primary);
}

.menu-no-results__sub {
    max-width: 240px;

    font-size: 14px;
    line-height: 1.4;
    color: var(--text-secondary);
}
</style>
